export function SellMyApartmentToInvestors() {
  return (
    <section className="py-20 bg-white" id="sell-to-investors">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Sell My Apartment to Investors - Smart Exit Strategy
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real estate investors offer unique advantages for apartment building sellers including speed, certainty, 
            and the ability to purchase properties in any condition without traditional financing hurdles.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Why Investors Are Ideal Buyers</h3>
            <p className="text-gray-600 mb-8">
              Professional real estate investors understand apartment buildings, move quickly, and can handle 
              complex deals that might scare away traditional homebuyers or inexperienced purchasers.
            </p>
            
            <div className="space-y-6">
              <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500">
                <h4 className="text-lg font-semibold text-blue-800 mb-3">
                  <i className="ri-eye-line mr-2"></i>
                  They See Potential, Not Problems
                </h4>
                <p className="text-gray-700">
                  Investors look at properties through a renovation and income lens. What you see as expensive 
                  problems, they see as value-add opportunities.
                </p>
              </div>

              <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
                <h4 className="text-lg font-semibold text-green-800 mb-3">
                  <i className="ri-speed-line mr-2"></i>
                  Fast Decision Making
                </h4>
                <p className="text-gray-700">
                  Professional investors can evaluate and make offers within 24-48 hours because they 
                  understand the numbers and have systems in place.
                </p>
              </div>

              <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-500">
                <h4 className="text-lg font-semibold text-purple-800 mb-3">
                  <i className="ri-money-dollar-circle-line mr-2"></i>
                  Cash or Creative Financing
                </h4>
                <p className="text-gray-700">
                  Many investors have cash available or access to private lending, eliminating traditional 
                  mortgage approval delays and complications.
                </p>
              </div>

              <div className="bg-orange-50 p-6 rounded-lg border-l-4 border-orange-500">
                <h4 className="text-lg font-semibold text-orange-800 mb-3">
                  <i className="ri-handshake-line mr-2"></i>
                  Experienced in Complex Deals
                </h4>
                <p className="text-gray-700">
                  Investors regularly handle tenant issues, property management transfers, and complicated 
                  closing situations that overwhelm other buyers.
                </p>
              </div>
            </div>
          </div>
          
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/5d8f2eb6912b3a5a36901d0817a86132.jpg"
              alt="Real estate investor analyzing apartment investment"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Types of Real Estate Investors
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-hammer-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Fix & Flip Investors</h4>
              <p className="text-gray-600 mb-4">
                Specialize in purchasing properties, renovating them quickly, and reselling for a profit 
                within 6-18 months.
              </p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-blue-800 mb-2">Best For:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Properties needing major updates</li>
                  <li>• Quick cash sales</li>
                  <li>• Below-market pricing acceptable</li>
                </ul>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-building-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Buy & Hold Investors</h4>
              <p className="text-gray-600 mb-4">
                Purchase properties for long-term rental income and appreciation. Focus on cash flow 
                and building wealth over time.
              </p>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-green-800 mb-2">Best For:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Income-producing properties</li>
                  <li>• Stable tenant base</li>
                  <li>• Fair market pricing</li>
                </ul>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-funds-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Investment Funds</h4>
              <p className="text-gray-600 mb-4">
                Private equity firms and real estate funds with significant capital looking for 
                larger apartment complexes and multifamily portfolios.
              </p>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-purple-800 mb-2">Best For:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Large apartment complexes (50+ units)</li>
                  <li>• Institutional-quality properties</li>
                  <li>• Premium pricing potential</li>
                </ul>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-team-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">REIT Companies</h4>
              <p className="text-gray-600 mb-4">
                Real Estate Investment Trusts that purchase apartment buildings to add to their 
                publicly-traded property portfolios.
              </p>
              <div className="bg-orange-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-orange-800 mb-2">Best For:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Class A properties</li>
                  <li>• Prime locations</li>
                  <li>• Professional management in place</li>
                </ul>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-user-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Individual Investors</h4>
              <p className="text-gray-600 mb-4">
                High-net-worth individuals or small investment groups looking to diversify their 
                portfolios with real estate assets.
              </p>
              <div className="bg-teal-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-teal-800 mb-2">Best For:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Mid-size buildings (10-50 units)</li>
                  <li>• Personal relationships</li>
                  <li>• Flexible terms possible</li>
                </ul>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-home-heart-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Distressed Asset Buyers</h4>
              <p className="text-gray-600 mb-4">
                Specialists in purchasing troubled properties including those facing foreclosure, 
                bankruptcy, or significant financial distress.
              </p>
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-red-800 mb-2">Best For:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Emergency sale situations</li>
                  <li>• Below-market opportunities</li>
                  <li>• Quick closing required</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">What Investors Look For</h3>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
                <h4 className="text-lg font-semibold text-green-800 mb-3">Cash Flow Potential</h4>
                <p className="text-gray-600 mb-3">
                  Current rental income vs. expenses, and potential for rent increases or expense reductions 
                  through better management.
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Rent rolls and operating statements</li>
                  <li>• Market rent comparisons</li>
                  <li>• Expense reduction opportunities</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
                <h4 className="text-lg font-semibold text-blue-800 mb-3">Value-Add Opportunities</h4>
                <p className="text-gray-600 mb-3">
                  Ways to increase the property's value through improvements, better management, 
                  or repositioning in the market.
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Unit renovation potential</li>
                  <li>• Common area improvements</li>
                  <li>• Management efficiency gains</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500">
                <h4 className="text-lg font-semibold text-purple-800 mb-3">Location and Market</h4>
                <p className="text-gray-600 mb-3">
                  Neighborhood trends, future development plans, transportation access, and overall 
                  market appreciation potential.
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Job growth and demographics</li>
                  <li>• School districts and amenities</li>
                  <li>• Future development plans</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=Real%20estate%20investor%20team%20meeting%20reviewing%20apartment%20building%20investment%20analysis%20with%20laptops%20financial%20charts%20and%20property%20photos%20professional%20conference%20room&width=800&height=600&seq=sell-apt-meeting-13&orientation=landscape"
              alt="Investor team analyzing apartment building investment"
              className="w-full h-96 object-cover rounded-xl shadow-lg mb-6"
            />
            
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-yellow-800 mb-3">
                <i className="ri-lightbulb-line mr-2"></i>
                Prepare for Investor Buyers
              </h4>
              <p className="text-gray-700 mb-4">
                Have these documents ready to impress serious investors and speed up the sale process:
              </p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Last 3 years of operating statements</li>
                <li>• Current rent roll with lease terms</li>
                <li>• Capital improvements history</li>
                <li>• Property tax and insurance records</li>
                <li>• Recent property inspection reports</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 rounded-xl p-12 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            How to Attract Quality Investors
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-chart-line text-2xl text-white"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Professional Marketing</h4>
              <p className="text-sm text-gray-600">
                Create investor-focused marketing materials highlighting cash flow, cap rates, and upside potential.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-price-tag-3-line text-2xl text-white"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Competitive Pricing</h4>
              <p className="text-sm text-gray-600">
                Price based on comparable sales and cap rates to attract immediate investor attention and offers.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-group-line text-2xl text-white"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Network Access</h4>
              <p className="text-sm text-gray-600">
                Work with agents who specialize in investment properties and have investor databases.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-2xl text-white"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Quick Response</h4>
              <p className="text-sm text-gray-600">
                Be prepared to respond quickly to investor inquiries and provide requested documentation immediately.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-12 text-center text-white">
          <h3 className="text-3xl font-bold mb-4">
            We Are Professional Real Estate Investors
          </h3>
          <p className="text-xl mb-8 max-w-4xl mx-auto">
            As experienced apartment building investors, we understand the numbers, can move quickly, 
            and offer fair prices based on true market value and investment potential.
          </p>
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-2xl font-bold mb-1">15+ Years</div>
              <div className="text-sm">Investment Experience</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-2xl font-bold mb-1">500+</div>
              <div className="text-sm">Properties Acquired</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-2xl font-bold mb-1">$200M+</div>
              <div className="text-sm">Transaction Volume</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-2xl font-bold mb-1">24 Hours</div>
              <div className="text-sm">Offer Response</div>
            </div>
          </div>
          <button className="bg-white text-indigo-600 px-12 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 whitespace-nowrap">
            Connect with Professional Investors
          </button>
        </div>
      </div>
    </section>
  );
}